import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { fetchMe, fetchMessages, OPEN_ROOM_REFETCH_MS } from '../lib/api';
import type { ApiError, SpaceMember } from '../lib/api';
import { resolveSender } from '../lib/spaceIdentity';
import { groupMessagesByDay } from '../lib/dayDivider';
import { format, t } from '../i18n';
import { useMentionNotifier } from '../hooks/useMentionNotifier';
import { EmptyState } from './EmptyState';
import { Message } from './Message';
import { Skeleton } from './Skeleton';

interface MessageThreadProps {
  roomId: string;
  searchQuery?: string;
  onReply?: (message: import('../lib/api').Message) => void;
  /** 'space' (default) or 'dm' — selects context-appropriate empty-state copy. */
  variant?: 'space' | 'dm';
  /** For the DM variant: the peer's friendly name, woven into the empty state. */
  peerName?: string;
  /**
   * True when messaging is disabled (archived / non-active space). The empty
   * state must not claim "the composer below sends as you" when it can't.
   */
  messagingDisabled?: boolean;
  /** The space roster, used to resolve each sender's display name + glyph.
   *  Absent on the DM variant, where bylines fall back to the raw handle. */
  members?: SpaceMember[];
}

/**
 * Context-aware empty-state description for a thread with zero messages.
 * An active space invites you to send; an archived space cannot; a DM reads
 * as the start of a 1:1 conversation.
 */
function emptyThreadDescription(
  variant: 'space' | 'dm',
  peerName: string | undefined,
  messagingDisabled: boolean,
): string {
  if (messagingDisabled) return t.emptyState.messagesArchivedDescription;
  if (variant === 'dm') {
    return peerName
      ? format(t.emptyState.messagesDmNoneDescriptionTemplate, { name: peerName })
      : t.emptyState.messagesDmNoneDescription;
  }
  return t.emptyState.messagesNoneDescription;
}

export function MessageThread({
  roomId,
  searchQuery,
  onReply,
  variant = 'space',
  peerName,
  messagingDisabled = false,
  members,
}: MessageThreadProps) {
  const {
    data,
    isLoading,
    isError,
    isFetching,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['space', roomId, 'messages'],
    queryFn: ({ pageParam }) =>
      fetchMessages(roomId, pageParam as string | undefined),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    // Fail fast on 4xx (not found / forbidden): retrying can't fix a client
    // error. Still retry transient 5xx / network failures a couple of times
    // before surfacing the error. Mirrors SpaceView's bounded-retry predicate
    // (T-0092) so a persistent read 5xx surfaces an error + Retry instead of a
    // perpetual loading skeleton.
    retry: (failureCount, error) => {
      const status = (error as unknown as ApiError | undefined)?.status;
      if (typeof status === 'number' && status >= 400 && status < 500) {
        return false;
      }
      return failureCount < 2;
    },
    // No websocket yet — poll the open room so new messages appear live, but
    // ONLY once the thread has actually loaded (T-0092). Polling a thread that
    // has never loaded re-runs the fetch, and a no-data refetch flips the query
    // out of its error state back to pending — re-masking a persistent read 5xx
    // under the skeleton on a loop. Gating the poll on `data` lets a failed
    // first load settle to a STABLE error + Retry (the user's Retry, matching
    // SpaceView, is the recovery path); live polling resumes once loaded.
    refetchInterval: (query) =>
      query.state.data ? OPEN_ROOM_REFETCH_MS : false,
  });

  // Single shared `/api/me` fetch (react-query dedupes by key) so each Message
  // can gate its Delete action on ownership without its own request (#1257).
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: fetchMe });

  const allMessages = useMemo(
    () =>
      data?.pages
        .slice()
        .reverse()
        .flatMap((p) => p.messages) ?? [],
    [data],
  );

  const displayMessages = useMemo(() => {
    const normalizedQuery = searchQuery?.trim();
    if (!normalizedQuery) return allMessages;
    const lower = normalizedQuery.toLowerCase();
    return allMessages.filter(
      (m) =>
        m.content.toLowerCase().includes(lower) ||
        m.sender_persona.toLowerCase().includes(lower),
    );
  }, [allMessages, searchQuery]);
  const hasActiveSearch = Boolean(searchQuery?.trim());

  // Interleave day dividers so the thread reads as dated sections.
  const grouped = useMemo(
    () => groupMessagesByDay(displayMessages),
    [displayMessages],
  );

  const bottomAnchor = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLOListElement>(null);
  const previousCount = useRef<number>(0);
  const [liveAnnouncement, setLiveAnnouncement] = useState('');

  useMentionNotifier(roomId, allMessages);

  useEffect(() => {
    bottomAnchor.current?.scrollIntoView({ behavior: 'smooth' });
  }, [allMessages.length]);

  useEffect(() => {
    const messages = allMessages;
    const newCount = messages.length;
    if (newCount > previousCount.current && previousCount.current > 0) {
      const newOnes = messages.slice(previousCount.current);
      const summary = newOnes
        .map((m) => `${m.sender_persona}: ${m.content.slice(0, 80)}`)
        .join('. ');
      setLiveAnnouncement(`New message — ${summary}`);
    }
    previousCount.current = newCount;
  }, [allMessages]);

  const handleLoadOlder = () => {
    if (!listRef.current) {
      void fetchNextPage();
      return;
    }
    const prevHeight = listRef.current.scrollHeight;
    const prevScrollTop = listRef.current.scrollTop;
    fetchNextPage().then(() => {
      if (!listRef.current) return;
      const delta = listRef.current.scrollHeight - prevHeight;
      listRef.current.scrollTop = prevScrollTop + delta;
    });
  };

  // Check the error branch BEFORE the loading branch (T-0092): a background
  // poll-refetch (refetchInterval) flips `isLoading` true again while an
  // earlier read is still failing, which would re-mask the error under the
  // skeleton forever. Gating the skeleton on first-load only (error wins over a
  // refetch-driven loading flip) keeps the error + Retry stable on a persistent
  // 5xx — on space threads AND DMs (MessageThread is shared).
  if (isError || (!data && !isLoading)) {
    return (
      <div className="message-thread-error" role="alert">
        <p className="message-thread-error-message">Could not load messages.</p>
        <button
          type="button"
          className="message-thread-error-retry btn-secondary"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          {isFetching ? 'Retrying…' : 'Retry'}
        </button>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <Skeleton
        className="message-thread-loading"
        ariaLabel="Loading messages"
        rows={3}
        variant="text"
      />
    );
  }

  if (allMessages.length === 0) {
    return (
      <EmptyState
        className="message-thread-empty"
        icon="💬"
        title={t.emptyState.messagesNoneTitle}
        description={emptyThreadDescription(variant, peerName, messagingDisabled)}
      />
    );
  }

  if (hasActiveSearch && displayMessages.length === 0) {
    return (
      <EmptyState
        className="message-thread-empty message-thread-search-empty"
        icon="🔍"
        title="No matching messages"
        description="Try another term or clear search to see the full conversation."
        action={
          hasNextPage ? (
            <button
              type="button"
              className="message-thread-load-older-btn"
              onClick={handleLoadOlder}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? 'Loading…' : 'Load older messages'}
            </button>
          ) : undefined
        }
      />
    );
  }

  return (
    <>
      <ol className="message-thread" aria-label="Messages" ref={listRef}>
        {hasNextPage && (
          <li className="message-thread-load-older">
            <button
              className="message-thread-load-older-btn"
              onClick={handleLoadOlder}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? 'Loading…' : 'Load older messages'}
            </button>
          </li>
        )}
        {grouped.map((entry) =>
          entry.kind === 'divider' ? (
            // A plain <li> (no role override): the <ol class="message-thread">
            // must contain only list items, so role="separator" here trips
            // axe's WCAG 1.3.1 "list" rule. The date text carries the meaning.
            <li key={entry.id} className="message-thread-day-divider">
              <span>{entry.label}</span>
            </li>
          ) : (
            <Message
              key={entry.message.message_id}
              message={entry.message}
              onReply={onReply}
              currentUserId={me?.user_id}
              sender={
                members
                  ? resolveSender(entry.message, members, me?.user_id)
                  : undefined
              }
            />
          ),
        )}
        <div ref={bottomAnchor} aria-hidden="true" />
      </ol>
      <div
        className="message-thread-live"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {liveAnnouncement}
      </div>
    </>
  );
}
